/**
 * Calendar Utilities
 * Helper functions for date/time formatting and calendar operations
 */

/**
 * Format a date string to readable format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date (e.g., "January 15, 2024")
 */
export const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Format a date string to time format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted time (e.g., "2:00 PM")
 */
export const formatTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Format duration in milliseconds to readable string
 * @param {number} milliseconds - Duration in milliseconds
 * @returns {string} Formatted duration (e.g., "1h 30m")
 */
export const formatDuration = (milliseconds) => {
  if (!milliseconds || milliseconds < 0) return "0m";

  const minutes = Math.floor(milliseconds / 60000);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0) {
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  }
  return `${minutes}m`;
};

/**
 * Format month and year
 * @param {Date} date - Date object
 * @returns {string} Formatted month year (e.g., "January 2025")
 */
export const formatMonthYear = (date) => {
  if (!date) return "";
  return date.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
};

/**
 * Get icon for event type
 * @param {string} type - Event type
 * @returns {string} Emoji icon
 */
export const getEventTypeIcon = (type) => {
  const icons = {
    meeting: "📞",
    deadline: "⏰",
    presentation: "📊",
    discussion: "💬",
    workshop: "🎓",
    milestone: "🎯",
    reminder: "🔔",
    other: "📅",
  };
  return icons[type] || "📅";
};

/**
 * Get label for RSVP status
 * @param {string} rsvp - RSVP status
 * @returns {string} Human-readable label
 */
export const getRSVPLabel = (rsvp) => {
  const labels = {
    pending: "⏳ Pending",
    accepted: "✅ Going",
    declined: "❌ Not Going",
    tentative: "❓ Maybe",
  };
  return labels[rsvp] || "Not Responded";
};

/**
 * Generate days array for a calendar month
 * @param {number} year - Year
 * @param {number} month - Month (0-11)
 * @returns {Array<number|null>} Array of day numbers with null for empty cells
 */
export const getDaysInMonth = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const daysInMonth = lastDay.getDate();
  const startDayOfWeek = firstDay.getDay(); // 0 = Sunday

  const days = [];

  // Empty cells before first day
  for (let i = 0; i < startDayOfWeek; i++) {
    days.push(null);
  }

  // Days of month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  return days;
};

/**
 * Get events for a specific day
 * @param {Array<Object>} events - Array of events
 * @param {Date} date - Target date
 * @returns {Array<Object>} Events occurring on that day
 */
export const getEventsForDay = (events, date) => {
  if (!events || !date) return [];

  return events.filter((event) => {
    const eventDate = new Date(event.startDate);
    return (
      eventDate.getDate() === date.getDate() &&
      eventDate.getMonth() === date.getMonth() &&
      eventDate.getFullYear() === date.getFullYear()
    );
  });
};

/**
 * Check if a date is today
 * @param {Date} date - Date to check
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
  if (!date) return false;
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * Check if event is happening now
 * @param {Object} event - Event object
 * @returns {boolean} True if event is currently happening
 */
export const isEventHappening = (event) => {
  if (!event) return false;
  const now = new Date();
  const start = new Date(event.startDate);
  const end = new Date(event.endDate);
  return now >= start && now <= end;
};

/**
 * Check if event is in the past
 * @param {Object} event - Event object
 * @returns {boolean} True if event has ended
 */
export const isEventPast = (event) => {
  if (!event) return false;
  const now = new Date();
  const end = new Date(event.endDate);
  return now > end;
};

/**
 * Check if user can join meeting (15 min before to 30 min after)
 * @param {Object} event - Event object
 * @returns {Object} { canJoin: boolean, message: string, minutesUntil: number }
 */
export const canJoinMeeting = (event) => {
  if (!event)
    return { canJoin: false, message: "Invalid event", minutesUntil: 0 };

  const now = new Date();
  const start = new Date(event.startDate);
  const end = new Date(event.endDate);

  const minutesUntilStart = Math.floor((start - now) / 60000);
  const minutesAfterEnd = Math.floor((now - end) / 60000);

  // Can join 15 min before to 30 min after
  const canJoinBefore = minutesUntilStart <= 15 && minutesUntilStart >= 0;
  const canJoinDuring = now >= start && now <= end;
  const canJoinAfter = minutesAfterEnd <= 30 && minutesAfterEnd >= 0;

  if (canJoinBefore || canJoinDuring || canJoinAfter) {
    return { canJoin: true, message: "Join now", minutesUntil: 0 };
  }

  if (minutesUntilStart > 15) {
    return {
      canJoin: false,
      message: `Meeting starts in ${minutesUntilStart} minutes`,
      minutesUntil: minutesUntilStart,
    };
  }

  return { canJoin: false, message: "Meeting has ended", minutesUntil: 0 };
};

/**
 * Convert datetime-local input value to ISO string
 * @param {string} localDateTime - Local datetime string from input
 * @returns {string} ISO string
 */
export const localToISO = (localDateTime) => {
  if (!localDateTime) return "";
  return new Date(localDateTime).toISOString();
};

/**
 * Convert ISO string to datetime-local input value
 * @param {string} isoString - ISO date string
 * @returns {string} Local datetime string for input
 */
export const isoToLocal = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Get time until event starts (for notifications)
 * @param {Object} event - Event object
 * @returns {string} Human-readable time until event
 */
export const getTimeUntilEvent = (event) => {
  if (!event) return "";

  const now = new Date();
  const start = new Date(event.startDate);
  const diff = start - now;

  if (diff < 0) return "Started";

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `in ${days} day${days > 1 ? "s" : ""}`;
  if (hours > 0) return `in ${hours} hour${hours > 1 ? "s" : ""}`;
  if (minutes > 0) return `in ${minutes} minute${minutes > 1 ? "s" : ""}`;
  return "starting soon";
};

export default {
  formatDate,
  formatTime,
  formatDuration,
  formatMonthYear,
  getEventTypeIcon,
  getRSVPLabel,
  getDaysInMonth,
  getEventsForDay,
  isToday,
  isEventHappening,
  isEventPast,
  canJoinMeeting,
  localToISO,
  isoToLocal,
  getTimeUntilEvent,
};
